import { inject, Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Status } from '@df/core/status';
import { PwaIntegrationService } from '@df/module-pwa/pwa-integration.service';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LocalCustomerModel } from '../../customer/customer.model.decorator';
import { NUserNotificationModal } from './modal/user-notification-modal.component';

/**
 * Namespace containing all user notification related interfaces and types
 */
export namespace NUserNotifications {
    /**
     * Core notification data structure
     */
    export interface INotification {
        id: string;
        type: 'homepage';
        title: string;
        subtitle?: string;
        image?: string;
        link?: string;
        date: Date;
    }

    /**
     * Get notifications list namespace
     */
    export namespace GetList {
        export const path = 'user-notifications/list';

        export interface IRequest {
            loyaltyId: string;
        }

        export interface IResponse {
            notifications: INotification[];
        }
    }

    /**
     * Get unread count namespace
     */
    export namespace GetUnreadCount {
        export const path = 'user-notifications/unread-count';

        export interface IRequest {
            loyaltyId: string;
            type?: 'homepage';
        }

        export interface IResponse {
            count: number;
        }
    }

    /**
     * Mark notifications as seen namespace
     */
    export namespace MarkSeen {
        export const path = 'user-notifications/mark-seen';

        export interface IRequest {
            loyaltyId: string;
            ids: string[];
        }

        export interface IResponse {
            modifiedCount: number;
        }
    }

    /**
     * Add new notification namespace
     */
    export namespace Add {
        export const path = 'user-notifications/add';

        export interface IRequest extends Omit<INotification, 'id' | 'date'> {
            loyaltyId: string;
        }

        export interface IResponse {
            notificationId: string;
        }
    }
}

declare module '@df/core/app' {
    interface App {
        userNotifications: UserNotificationsService;
    }
}

/**
 * Service for managing user notifications through the API
 */
@Injectable({
    providedIn: 'root'
})
export class UserNotificationsService {
    /**
     * Services
     */
    private readonly requestService = inject(RequestService);
    private readonly timeoutService = injectTimeoutService();
    private readonly customerModel = inject(CustomerModel);
    private readonly meta = inject(Meta);
    private readonly dfModalService = inject(DfModalService);
    private readonly pwaIntegrationService = inject(PwaIntegrationService);

    readonly notifications$ = new BehaviorSubject<NUserNotifications.INotification[]>([]);

    private isDaemonRunning = false;

    private subscriptions = new Subscription();

    /**
     * Constructor
     */
    constructor(app: App) {
        app.userNotifications = this;

        if (this.pwaIntegrationService.isStandalone) {
            this.runDaemon();
            this.subscriptions.add(
                this.meta.subscribe(() => {
                    this.showNotificationsIfNeeded();
                })
            );
        }
    }

    /**
     * Get list of user notifications
     */
    getNotifications(
        status: Status | undefined,
        request: Omit<NUserNotifications.GetList.IRequest, 'loyaltyId'>
    ): Promise<NUserNotifications.GetList.IResponse> {
        if (!this.loyaltyId) {
            return Promise.resolve({ notifications: [] });
        }
        const data: NUserNotifications.GetList.IRequest = {
            loyaltyId: this.loyaltyId,
            ...request
        };
        return this.requestService.send(
            status,
            {
                path: NUserNotifications.GetList.path,
                data,
                api: 'node'
            },
            undefined,
            undefined,
            { method: 'GET' }
        );
    }

    /**
     * Get count of unread notifications
     */
    getUnreadCount(
        status: Status | undefined,
        request: Omit<NUserNotifications.GetUnreadCount.IRequest, 'loyaltyId'>
    ): Promise<NUserNotifications.GetUnreadCount.IResponse> {
        if (!this.loyaltyId) {
            return Promise.resolve({ count: 0 });
        }
        const data: NUserNotifications.GetUnreadCount.IRequest = {
            loyaltyId: this.loyaltyId,
            ...request
        };
        return this.requestService.send(
            status,
            {
                path: NUserNotifications.GetUnreadCount.path,
                data,
                api: 'node'
            },
            undefined,
            undefined,
            { method: 'GET' }
        );
    }

    /**
     * Mark notifications as seen
     */
    markNotificationsAsSeen(
        status: Status | undefined,
        request: Omit<NUserNotifications.MarkSeen.IRequest, 'loyaltyId'>
    ): Promise<NUserNotifications.MarkSeen.IResponse> {
        if (!this.loyaltyId) {
            return Promise.resolve({ modifiedCount: 0 });
        }
        const data: NUserNotifications.MarkSeen.IRequest = {
            loyaltyId: this.loyaltyId,
            ...request
        };
        return this.requestService.send(
            status,
            {
                path: NUserNotifications.MarkSeen.path,
                data,
                api: 'node'
            },
            () => {
                this.notifications$.next(this.notifications$.value.filter(n => !request.ids.includes(n.id)));
            },
            undefined,
            { method: 'POST' }
        );
    }

    /**
     * Add new notification
     */
    addNotification(
        status: Status | undefined,
        request: NUserNotifications.Add.IRequest,
        accessKey: string
    ): Promise<NUserNotifications.Add.IResponse> {
        return this.requestService.send(
            status,
            {
                path: NUserNotifications.Add.path,
                data: request,
                api: 'node'
            },
            undefined,
            undefined,
            {
                method: 'POST',
                headers: {
                    'X-Authorization': `Bearer ${accessKey}`
                }
            }
        );
    }

    /**
     * Manually trigger notifications update (e.g., when app resumes)
     */
    async refreshNotifications(): Promise<void> {
        if (!this.isDaemonRunning) {
            try {
                await this.updateNotifications();
            } catch (e) {
                console.error('Error refreshing notifications', e);
            }
        }
    }

    private async runDaemon() {
        if (this.isDaemonRunning) {
            return;
        }

        this.isDaemonRunning = true;
        try {
            await this.updateNotifications();
        } catch (e) {
            console.error('Error updating notifications', e);
        } finally {
            this.isDaemonRunning = false;
        }
        this.timeoutService.setTimeout(() => this.runDaemon(), 60000);
    }

    private get loyaltyId(): string | undefined {
        return this.customerModel.isLoggedIn ? (this.customerModel as LocalCustomerModel).loyaltyId : undefined;
    }

    private async updateNotifications() {
        const loyaltyId = this.loyaltyId;
        if (!loyaltyId) {
            if (this.notifications$.value.length > 0) {
                this.notifications$.next([]);
            }
            return;
        }

        const notifications = await this.getNotifications(undefined, {});
        this.notifications$.next(notifications.notifications);
        this.showNotificationsIfNeeded();
    }

    private showNotificationsIfNeeded() {
        const type = this.retriveExpectedNotificationsType();
        if (!type) {
            return;
        }
        const notifications = this.notifications$.value.filter(n => n.type === type);
        if (notifications.length > 0) {
            // Show all notifications in a single modal
            this.dfModalService.open(undefined, NUserNotificationModal.MODAL_ID, undefined, { notifications });
        }
    }

    private retriveExpectedNotificationsType(): NUserNotifications.INotification['type'] | undefined {
        const model = this.meta.model;
        if (!model || !model.data) {
            return undefined;
        }
        if (model.type === 'page' && ['', '/'].includes(model.data.url!)) {
            return 'homepage';
        }
        return undefined;
    }
}
